import React, {
  FunctionComponent,
  useEffect,
  useRef,
  KeyboardEvent,
} from 'react';
import { IconColor, IconName } from 'app/common/components/controls';
import { IconButton } from 'app/common/components/controls/button/button';
import classNames from 'classnames';
import styles from './Dialog.less';

interface DialogProps {
  title: React.ReactNode;
  tip?: React.ReactNode;
  closeDialog: () => void;
  isCentered?: boolean;
  image?: HTMLElement;
  mobileImage?: HTMLElement;
  onKeyPressCapture?: (e: KeyboardEvent) => void;
}

export const Dialog: FunctionComponent<DialogProps> = ({
  title,
  tip,
  children,
  closeDialog,
  isCentered,
  image,
  mobileImage,
  onKeyPressCapture,
}) => {
  const refIllustrationFull = useRef<HTMLDivElement>(null);
  const refIllustrationMobile = useRef<HTMLDivElement>(null);

  // Append pre-loaded image on mount
  useEffect(() => {
    if (refIllustrationFull.current && image) {
      refIllustrationFull.current.appendChild(image);
    }
    if (refIllustrationMobile.current && mobileImage) {
      refIllustrationMobile.current.appendChild(mobileImage);
    }
  });

  return (
    <div
      /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */
      tabIndex={0}
      // auto-focusing so that we can receive keypress events
      ref={(div) => div && div.focus()}
      className={classNames(
        styles.wrapper,
        isCentered ? styles.centered : undefined,
      )}
      onKeyPressCapture={onKeyPressCapture}
    >
      <div
        className={classNames(
          styles.dialog,
          isCentered ? styles.centeredDialog : undefined,
        )}
      >
        {mobileImage && (
          <div
            className={styles.illustrationMobile}
            ref={refIllustrationMobile}
          ></div>
        )}
        <div className={styles.controls}>
          <IconButton
            icon={IconName.Close}
            className={styles.icon}
            color={IconColor.Dark}
            largeIcon
            onClick={closeDialog}
          ></IconButton>
        </div>
        <div
          className={
            image ? styles.contentWithImage : styles.contentWithoutImage
          }
        >
          <header className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
          </header>
          <div>{children}</div>
        </div>
        <div
          className={styles.illustrationFull}
          ref={refIllustrationFull}
        ></div>
      </div>
      {tip && (
        <div className={styles.tip}>
          <span role="img" aria-label="tip">
            💡
          </span>
          <span>{tip}</span>
        </div>
      )}
    </div>
  );
};
